var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../../Base.js";
import DomHelper from "../DomHelper.js";
import EventHelper from "../EventHelper.js";
import Delayable from "../../mixin/Delayable.js";
import Rectangle from "../util/Rectangle.js";
const noScroll = { pageXOffset: 0, pageYOffset: 0 };
var DragHelperTranslate_default = (Target) => {
  var _a;
  return _a = class extends Delayable(Target || Base) {
    static get configurable() {
      return {
        positioning: null,
        // Private config that disables updating elements position, for when data is live updated during drag,
        // leading to element being redrawn
        skipUpdatingElement: null
      };
    }
    //region Init
    /**
     * Initialize translation drag mode.
     * @private
     */
    initTranslateDrag() {
      const me = this;
      if (!me.isElementDraggable && me.targetSelector) {
        me.isElementDraggable = (element) => element.closest(me.targetSelector);
      }
    }
    //endregion
    //region Grab, update, finish
    /**
     * Grab an element which can be moved using translation.
     * @private
     * @param event
     * @returns {Boolean}
     */
    grabTranslateDrag(event) {
      const element = this.getTarget(event);
      if (element) {
        this.context = {
          valid: true,
          element,
          startPageX: event.pageX,
          startPageY: event.pageY,
          startClientX: event.clientX,
          startClientY: event.clientY
        };
        return true;
      }
      return false;
    }
    getTarget(event) {
      return event.target.closest(this.targetSelector);
    }
    getX(element) {
      if (this.positioning === "absolute") {
        return parseFloat(element.style.left, 10);
      }
      if (this.positioning === "inset") {
        return parseFloat(getComputedStyle(element).insetInlineStart, 10);
      }
      return DomHelper.getTranslateX(element);
    }
    getY(element) {
      if (this.positioning === "absolute") {
        return parseFloat(element.style.top, 10);
      }
      if (this.positioning === "inset") {
        return parseFloat(getComputedStyle(element).insetBlockStart, 10);
      }
      return DomHelper.getTranslateY(element);
    }
    getXY(element) {
      if (this.positioning === "absolute") {
        return [element.offsetLeft, element.offsetTop];
      }
      if (this.positioning === "inset") {
        const style = getComputedStyle(element);
        return [parseFloat(style.insetInlineStart, 10), parseFloat(style.insetBlockStart, 10)];
      }
      return DomHelper.getTranslateXY(element);
    }
    setXY(element, x, y) {
      if (this.skipUpdatingElement) {
        return;
      }
      if (this.positioning === "absolute") {
        element.style.left = x + "px";
        element.style.top = y + "px";
      } else if (this.positioning === "inset") {
        element.style.insetInlineStart = x + "px";
        element.style.insetBlockStart = y + "px";
      } else {
        DomHelper.setTranslateXY(element, x, y);
      }
    }
    /**
     * Start translating, called on first mouse move after dragging
     * @private
     * @param event
     */
    startTranslateDrag(event) {
      const me = this, { context, outerElement, proxySelector } = me, dragWithin = me.dragWithin = me.dragWithin || me.cloneTarget && document.body;
      let element = context.dragProxy || context.element;
      const grabbed = element, grabbedParent = element.parentElement;
      if (me.cloneTarget) {
        const elementToClone = proxySelector ? element.querySelector(proxySelector) : element;
        let { width, height, x: proxyX, y: proxyY } = Rectangle.from(elementToClone, Rectangle.outer(dragWithin));
        if (me.isRTL && element.style.insetInlineStart) {
          proxyX = Rectangle.outer(dragWithin).right - Rectangle.from(element).right;
        }
        element = me.createProxy(element);
        let x = proxyX, y = proxyY;
        if (me.autoSizeClonedTarget) {
          element.style.width = `${width}px`;
          element.style.height = `${height}px`;
        }
        element.classList.add(me.dragProxyCls, me.draggingCls);
        element.classList.remove("b-hover", "b-selected", "b-focused");
        dragWithin.appendChild(element);
        if (!me.autoSizeClonedTarget || proxySelector) {
          const proxyRect = element.getBoundingClientRect(), { x: dragWithinX, y: dragWithinY } = dragWithin.getBoundingClientRect(), localX = event.clientX - dragWithinX, localY = event.clientY - dragWithinY + (dragWithin !== document.body ? document.body.getBoundingClientRect().y : 0);
          x = localX - proxyRect.width / 2;
          y = localY - proxyRect.height / 2;
          context.startPageX = event.pageX;
          context.startPageY = event.pageY;
        }
        me.setXY(element, x, y);
        grabbed.classList.add("b-drag-original");
        if (me.hideOriginalElement) {
          grabbed.classList.add("b-hidden");
        }
      }
      element.classList.add(me.draggingCls);
      const elementStartX = me.getX(element), elementStartY = me.getY(element);
      Object.assign(context, {
        // The element which we're moving, could be a cloned version of grabbed, or the grabbed element itself
        element,
        // The original element upon which the mousedown event triggered a drag operation
        grabbed,
        // The parent of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedParent,
        // The next sibling of the original element where the pointerdown was detected - to be able to restore after an invalid drop
        grabbedNextSibling: element.nextElementSibling,
        // Elements position within parent element
        elementStartX,
        elementStartY,
        // The inset mode currently only supports dragging within the offsetParent element, to ensure that inset
        // coord can be accurately read without walking
        elementX: me.positioning === "inset" ? elementStartX : DomHelper.getOffsetX(element, dragWithin || outerElement),
        elementY: me.positioning === "inset" ? elementStartY : DomHelper.getOffsetY(element, dragWithin || outerElement),
        scrollX: 0,
        scrollY: 0,
        scrollManagerElementContainsDragProxy: !me.cloneTarget || dragWithin === outerElement
      });
      if (dragWithin) {
        context.parentElement = element.parentElement;
        if (dragWithin !== element.parentElement) {
          dragWithin.appendChild(element);
        }
        me.updateTranslateProxy(event);
      }
    }
    // When drag has started, create proxy versions (if applicable) and store original positions of all related elements
    // to be able to animate back to these positions in case of an aborted drag
    onTranslateDragStarted() {
      const me = this, { context } = me;
      let { relatedElements } = context;
      if (me.unifiedProxy) {
        context.element.classList.add("b-drag-main", "b-drag-unified-proxy");
      }
      if ((relatedElements == null ? void 0 : relatedElements.length) > 0) {
        context.relatedElStartPos = [];
        context.relatedElDragFromPos = [];
        const { proxySelector } = me;
        let [elementStartX, elementStartY] = [context.elementStartX, context.elementStartY];
        context.originalRelatedElements = relatedElements;
        relatedElements = context.relatedElements = relatedElements.map((relatedEl, i) => {
          const proxyTemplateElement = proxySelector ? relatedEl.querySelector(proxySelector) : relatedEl, { x, y, width, height } = Rectangle.from(proxyTemplateElement, me.dragWithin), relatedElementToDrag = me.cloneTarget ? me.createProxy(relatedEl) : relatedEl;
          relatedElementToDrag.classList.add(me.draggingCls);
          relatedElementToDrag.classList.remove("b-hover", "b-selected", "b-focused");
          if (me.cloneTarget) {
            me.setXY(relatedElementToDrag, x, y);
            me.dragWithin.appendChild(relatedElementToDrag);
            relatedElementToDrag.classList.add(me.dragProxyCls);
            if (me.autoSizeClonedTarget) {
              relatedElementToDrag.style.width = `${width}px`;
              relatedElementToDrag.style.height = `${height}px`;
            }
            if (me.hideOriginalElement) {
              relatedEl.classList.add("b-hidden");
            }
            relatedEl.classList.add("b-drag-original");
          }
          context.relatedElStartPos[i] = context.relatedElDragFromPos[i] = me.getXY(relatedElementToDrag);
          if (me.unifiedProxy) {
            relatedElementToDrag.classList.add("b-drag-unified-animation", "b-drag-unified-proxy");
            elementStartX += me.unifiedOffset;
            elementStartY += me.unifiedOffset;
            me.setXY(relatedElementToDrag, elementStartX, elementStartY);
            context.relatedElDragFromPos[i] = [elementStartX, elementStartY];
            relatedElementToDrag.style.zIndex = 100 - i;
          }
          return relatedElementToDrag;
        });
        if (me.unifiedProxy && relatedElements && relatedElements.length > 0) {
          EventHelper.onTransitionEnd({
            element: relatedElements[0],
            property: "transform",
            handler() {
              relatedElements.forEach((el) => el.classList.remove("b-drag-unified-animation"));
            },
            thisObj: me,
            once: true
          });
        }
      }
    }
    /**
     * Limit translation to outer bounds and specified constraints
     * @private
     * @param element
     * @param x
     * @param y
     * @returns {{constrainedX: *, constrainedY: *}}
     */
    applyConstraints(element, x, y) {
      const me = this, { constrain, dragWithin } = me, { pageXOffset, pageYOffset } = dragWithin === document.body ? globalThis : noScroll;
      if (dragWithin && constrain) {
        if (x < 0) {
          x = 0;
        }
        if (x + element.offsetWidth > dragWithin.scrollWidth) {
          x = dragWithin.scrollWidth - element.offsetWidth;
        }
        if (y < 0) {
          y = 0;
        }
        if (y + element.offsetHeight > dragWithin.scrollHeight) {
          y = dragWithin.scrollHeight - element.offsetHeight;
        }
      }
      if (typeof me.minX === "number") {
        x = Math.max(me.minX + pageXOffset, x);
      }
      if (typeof me.maxX === "number") {
        x = Math.min(me.maxX + pageXOffset, x);
      }
      if (typeof me.minY === "number") {
        y = Math.max(me.minY + pageYOffset, y);
      }
      if (typeof me.maxY === "number") {
        y = Math.min(me.maxY + pageYOffset, y);
      }
      return { constrainedX: x, constrainedY: y };
    }
    /**
     * Update elements translation on mouse move.
     * @private
     * @param {MouseEvent} event
     * @param {Object} scrollManagerConfig
     */
    updateTranslateProxy(event, scrollManagerConfig) {
      const me = this, { lockX, lockY, context } = me, element = context.dragProxy || context.element, { relatedElements, relatedElDragFromPos } = context, rtlInsetFlip = me.isRTL && me.positioning === "inset" ? -1 : 1;
      if (context.scrollManagerElementContainsDragProxy && scrollManagerConfig) {
        context.scrollX = scrollManagerConfig.getRelativeLeftScroll(element);
        context.scrollY = scrollManagerConfig.getRelativeTopScroll(element);
      }
      context.pageX = event.pageX;
      context.pageY = event.pageY;
      context.clientX = event.clientX;
      context.clientY = event.clientY;
      let newX = context.elementStartX + (event.pageX - context.startPageX + context.scrollX) * rtlInsetFlip, newY = context.elementStartY + event.pageY - context.startPageY + context.scrollY;
      if (me.snapCoordinates) {
        const snapped = me.snapCoordinates({ element, newX, newY });
        newX = snapped.x;
        newY = snapped.y;
      }
      const { constrainedX, constrainedY } = me.applyConstraints(element, newX, newY);
      if (context.started || constrainedX !== newX || constrainedY !== newY) {
        me.setXY(element, lockX ? void 0 : constrainedX, lockY ? void 0 : constrainedY);
      }
      if (relatedElements) {
        const deltaX = lockX ? 0 : constrainedX - context.elementStartX, deltaY = lockY ? 0 : constrainedY - context.elementStartY;
        relatedElements.forEach((r, i) => {
          const [x, y] = relatedElDragFromPos[i];
          me.setXY(r, x + deltaX, y + deltaY);
        });
      }
      context.newX = constrainedX;
      context.newY = constrainedY;
    }
    /**
     * Finalize drag, fire drop.
     * @private
     * @param event
     * @fires drop
     */
    async finishTranslateDrag(event) {
      const me = this, context = me.context, { target } = event, xChanged = !me.lockX && Math.round(context.newX) !== Math.round(context.elementStartX), yChanged = !me.lockY && Math.round(context.newY) !== Math.round(context.elementStartY), element = context.dragProxy || context.element, { relatedElements } = context;
      if (!me.ignoreSamePositionDrop || xChanged || yChanged) {
        if (context.valid === false) {
          await me.abortTranslateDrag(true, event);
        } else {
          const targetRect = !me.allowDropOutside && Rectangle.from(me.dragWithin || me.outerElement);
          if (targetRect && (typeof me.minX !== "number" && me.minX !== true && event.pageX < targetRect.left || typeof me.maxX !== "number" && me.maxX !== true && event.pageX > targetRect.right || typeof me.minY !== "number" && me.minY !== true && event.pageY < targetRect.top || typeof me.maxY !== "number" && me.maxY !== true && event.pageY > targetRect.bottom)) {
            await me.abortTranslateDrag(true, event);
          } else {
            context.finalize = async (valid = context.valid) => {
              if (context.finalized) {
                console.warn("DragHelper: Finalizing already finalized drag");
                return;
              }
              context.finalized = true;
              if (!valid && me.context) {
                await me.abortTranslateDrag(true, null, true);
              }
              if (!me.isDestroyed) {
                me.trigger("dropFinalized", { context, event, target });
                me.reset();
              }
              if (!me.cloneTarget && element.parentElement !== context.grabbedParent) {
                [element, ...relatedElements || []].forEach((el) => el.style.transform = "");
              }
            };
            context.async = false;
            await me.trigger("drop", { context, event, target });
            if (!context.async) {
              await context.finalize();
            }
          }
        }
      } else {
        me.abortTranslateDrag(false, event);
      }
    }
    /**
     * Abort translation
     * @private
     * @param invalid
     * @fires abort
     */
    async abortTranslateDrag(invalid = false, event = null, silent = false) {
      var _a2, _b;
      const me = this, {
        cloneTarget,
        context,
        proxySelector,
        dragWithin,
        draggingCls
      } = me, { relatedElements, relatedElStartPos, grabbed } = context, element = context.dragProxy || context.element;
      context.valid = false;
      (_a2 = me.scrollManager) == null ? void 0 : _a2.stopMonitoring();
      if (context.aborted) {
        console.warn("DragHelper: Aborting already aborted drag");
        return;
      }
      let { elementStartX, elementStartY } = context;
      const proxyMoved = elementStartX !== me.getX(element) || elementStartY !== me.getY(element);
      if (element && context.started) {
        if (!cloneTarget && dragWithin && dragWithin !== context.grabbedParent) {
          context.grabbedParent.insertBefore(element, context.grabbedNextSibling);
        }
        if (cloneTarget) {
          if (proxySelector) {
            const animateTo = grabbed.querySelector(proxySelector) || grabbed, { x, y } = Rectangle.from(animateTo);
            elementStartX = x;
            elementStartY = y;
          }
        }
        element.classList.add("b-aborting");
        me.setXY(element, elementStartX, elementStartY);
        relatedElements == null ? void 0 : relatedElements.forEach((element2, i) => {
          element2.classList.remove(draggingCls);
          element2.classList.add("b-aborting");
          me.setXY(element2, relatedElStartPos[i][0], relatedElStartPos[i][1]);
        });
        if (!silent) {
          me.trigger(invalid ? "drop" : "abort", { context, event });
        }
        if (element.isConnected && !me.isDestroying && proxyMoved) {
          const transitionProperty = me.positioning === "inset" ? "inset" : "transform";
          await EventHelper.waitForTransitionEnd({
            element,
            property: DomHelper.getPropertyTransitionDuration(element, transitionProperty) ? transitionProperty : "all",
            thisObj: me,
            once: true,
            timeout: 300,
            runOnDestroy: true
          });
        }
        if (!me.isDestroyed) {
          me.trigger("abortFinalized", { context, event });
        }
      }
      if ((_b = me.context) == null ? void 0 : _b.started) {
        me.reset();
      }
    }
    // Restore state of all mutated elements
    cleanUp() {
      const me = this, { context, cloneTarget, draggingCls, dragProxyCls } = me, element = context.dragProxy || context.element, { relatedElements, originalRelatedElements, grabbed } = context, removeClonedProxies = cloneTarget && (me.removeProxyAfterDrop || !context.valid), cssClassesToRemove = [draggingCls, "b-aborting", dragProxyCls, "b-drag-main", "b-drag-unified-proxy"];
      element.classList.remove(...cssClassesToRemove);
      if (removeClonedProxies) {
        element.remove();
      }
      relatedElements == null ? void 0 : relatedElements.forEach((element2) => {
        if (removeClonedProxies) {
          element2.remove();
        } else {
          element2.classList.remove(...cssClassesToRemove);
        }
      });
      grabbed.classList.remove("b-drag-original", "b-hidden");
      originalRelatedElements == null ? void 0 : originalRelatedElements.forEach((element2) => element2.classList.remove("b-hidden", "b-drag-original"));
    }
    //endregion
  }, __publicField(_a, "$name", "DragHelperTranslate"), _a;
};
export {
  DragHelperTranslate_default as default
};
